import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../config';
import {
  Rocket,
  Shield,
  Key,
  Database,
  Lock,
  Cpu,
  Zap,
  CheckCircle2,
  ArrowRight,
  Copy,
  BookOpen,
  FileCode,
  Compass,
  RefreshCw,
  Check,
  Clock,
  Sparkles
} from 'lucide-react';
import './GettingStarted.css';

const GettingStarted = () => {
  const { tenant, token, isAdmin, isManager } = useAuth();
  const [activeTab, setActiveTab] = useState('workflow'); // 'workflow' | 'capabilities' | 'quickstart'
  const [activeLang, setActiveLang] = useState('curl'); // 'curl' | 'python' | 'node' | 'java'
  const [apiKey, setApiKey] = useState('');
  const [copied, setCopied] = useState(false);

  // Dynamic live onboarding status state
  const [onboarding, setOnboarding] = useState({
    loading: true,
    refreshing: false,
    keyCount: 0,
    schemaCount: 0,
    policyCount: 0,
    auditCount: 0,
    step1Done: false,
    step2Done: false,
    step3Done: false,
    step4Done: false,
    step5Done: false,
    step6Done: false
  });

  const tenantUid = tenant?.uid || 'tenant-sample-uid';
  const effectiveKey = apiKey || 'vz_live_sample_apikey_7f8a9b';

  const fetchOnboardingStatus = useCallback(async () => {
    setOnboarding(prev => ({ ...prev, refreshing: true }));

    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (apiKey) headers['X-API-KEY'] = apiKey;
    if (tenant?.uid) headers['X-TENANT-ID'] = tenant.uid;

    let keyCount = 0;
    let schemaCount = 0;
    let policyCount = 0;
    let auditCount = 0;

    // 1. Identity Check
    const step1Done = Boolean(tenant?.uid);

    // 2. API Keys Check
    try {
      const res = await fetch(`${API_BASE}/dashboard/api-keys`, { headers });
      if (res.ok) {
        const keys = await res.json();
        if (Array.isArray(keys)) {
          keyCount = keys.length;
          if (keys.length > 0 && !apiKey) {
            const active = keys.find(k => k.enabled && !k.revoked) || keys[0];
            const val = active.apiKeyPreview || active.apiKey || active.maskedKey;
            if (val) setApiKey(val);
          }
        }
      }
    } catch (e) {}

    // 3. Schemas Check
    try {
      const res = await fetch(`${API_BASE}/admin/schemas?limit=20`, { headers });
      if (res.ok) {
        const schemas = await res.json();
        if (Array.isArray(schemas)) {
          schemaCount = schemas.length;
        }
      }
    } catch (e) {}

    // 4. Policies Check
    try {
      const res = await fetch(`${API_BASE}/admin/policies/page?page=0&size=20`, { headers });
      if (res.ok) {
        const pData = await res.json();
        const pList = pData?.content || (Array.isArray(pData) ? pData : []);
        policyCount = pList.length;
      }
    } catch (e) {}

    // 5. Traffic Check
    const tokensUsed = Number(tenant?.tokensUsedThisMonth || tenant?.tokensTotal || 0);
    const gatewayTested = localStorage.getItem('vaultize_gateway_tested') === 'true';
    const step5Done = tokensUsed > 0 || gatewayTested;

    // 6. Audit Logs Check
    try {
      const res = await fetch(`${API_BASE}/dashboard/audit-logs?page=0&size=5`, { headers });
      if (res.ok) {
        const aData = await res.json();
        const aList = aData?.content || (Array.isArray(aData) ? aData : []);
        auditCount = aList.length;
      }
    } catch (e) {}
    const step6Done = auditCount > 0 || tokensUsed > 0;

    setOnboarding({
      loading: false,
      refreshing: false,
      keyCount,
      schemaCount,
      policyCount,
      auditCount,
      step1Done,
      step2Done: keyCount > 0,
      step3Done: schemaCount > 0,
      step4Done: policyCount > 0,
      step5Done,
      step6Done
    });
  }, [token, apiKey, tenant?.uid, tenant?.tokensUsedThisMonth, tenant?.tokensTotal]);

  useEffect(() => {
    const savedApiKey = localStorage.getItem('vaultize_api_key');
    if (savedApiKey) {
      setApiKey(savedApiKey);
    }
    fetchOnboardingStatus();
  }, [fetchOnboardingStatus]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Progress Calculations
  const compulsorySteps = [onboarding.step1Done, onboarding.step2Done, onboarding.step5Done];
  const compulsoryDoneCount = compulsorySteps.filter(Boolean).length;
  const allCompulsoryDone = compulsoryDoneCount === 3;

  const allSteps = [
    onboarding.step1Done,
    onboarding.step2Done,
    onboarding.step3Done,
    onboarding.step4Done,
    onboarding.step5Done,
    onboarding.step6Done
  ];
  const totalDoneCount = allSteps.filter(Boolean).length;
  const progressPercent = Math.round((totalDoneCount / 6) * 100);

  const codeSnippets = {
    curl: `# 1. Format-Preserving Tokenization (FPE) of Credit Card
curl -X POST "${API_BASE}/tokenize" \\
  -H "X-API-KEY: ${effectiveKey}" \\
  -H "X-TENANT-ID: ${tenantUid}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "piiType": "card",
    "piiValue": "4532-1234-5678-9014",
    "formatPreserving": true,
    "preserveLuhn": true
  }'

# 2. Drop-in OpenAI Reverse Proxy with Live Streaming SSE
curl -N -X POST "${API_BASE}/gateway/llm/v1/chat/completions" \\
  -H "X-API-KEY: ${effectiveKey}" \\
  -H "X-TENANT-ID: ${tenantUid}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "gpt-4o",
    "messages": [
      {"role": "user", "content": "Doctor Jane (email: jane@hospital.org) diagnosed patient with SSN 123-45-6789."}
    ],
    "stream": true
  }'`,

    python: `import requests

API_BASE = "${API_BASE}"
HEADERS = {
    "X-API-KEY": "${effectiveKey}",
    "X-TENANT-ID": "${tenantUid}",
    "Content-Type": "application/json"
}

# 1. Tokenize sensitive customer record
fpe_payload = {
    "piiType": "card",
    "piiValue": "4532-1234-5678-9014",
    "formatPreserving": True,
    "preserveLuhn": True
}
res = requests.post(f"{API_BASE}/tokenize", json=fpe_payload, headers=HEADERS)
print("Vaultized Token:", res.json()["token"])

# 2. Drop-in OpenAI SDK Integration
from openai import OpenAI
client = OpenAI(
    base_url=f"{API_BASE}/gateway/llm/v1",
    api_key="${effectiveKey}"
)
response = client.chat.completions.create(
    model="gpt-4o",
    messages=[{"role": "user", "content": "Patient John Doe (SSN: 987-65-4321, email: j@med.org)"}],
    stream=True
)
for chunk in response:
    print(chunk.choices[0].delta.content or "", end="", flush=True)`,

    node: `const axios = require('axios');

const API_BASE = '${API_BASE}';
const headers = {
  'X-API-KEY': '${effectiveKey}',
  'X-TENANT-ID': '${tenantUid}',
  'Content-Type': 'application/json'
};

async function runVaultizeExample() {
  // 1. Format-Preserving Encryption for card data
  const fpeResponse = await axios.post(
    \`\${API_BASE}/tokenize\`,
    {
      piiType: 'card',
      piiValue: '4532-1234-5678-9014',
      formatPreserving: true,
      preserveLuhn: true
    },
    { headers }
  );
  console.log('FPE Tokenized Result:', fpeResponse.data.token);

  // 2. OpenAI SDK Pointed directly to Vaultize Reverse Proxy
  // const OpenAI = require('openai');
  // const client = new OpenAI({ baseURL: \`\${API_BASE}/gateway/llm/v1\`, apiKey: '${effectiveKey}' });
}

runVaultizeExample().catch(console.error);`,

    java: `// Vaultize Java SDK / Spring Boot WebClient Integration
import org.springframework.web.reactive.function.client.WebClient;

WebClient client = WebClient.builder()
    .baseUrl("${API_BASE}")
    .defaultHeader("X-API-KEY", "${effectiveKey}")
    .defaultHeader("X-TENANT-ID", "${tenantUid}")
    .defaultHeader("Content-Type", "application/json")
    .build();

// Tokenize Sensitive Card
String tokenResponse = client.post()
    .uri("/tokenize")
    .bodyValue(Map.of(
        "piiType", "card",
        "piiValue", "4532-1234-5678-9014",
        "formatPreserving", true,
        "preserveLuhn", true
    ))
    .retrieve()
    .bodyToMono(String.class)
    .block();

System.out.println("Tokenized Response: " + tokenResponse);`
  };

  const steps = [
    {
      step: 1,
      title: 'Tenant Verification & Organization Setup',
      desc: 'Verify your assigned Organization Tenant UID, verify security admin roles, and confirm tenant status.',
      requirement: 'COMPULSORY',
      reqReason: 'Mandatory: Binds all encryption operations and keys to your enterprise tenant isolation boundary.',
      link: '/profile',
      linkText: 'Check Profile & Tenant UID',
      icon: <Compass size={20} className="step-icon text-cyan" />,
      tag: 'Identity',
      isDone: onboarding.step1Done,
      doneBadge: `✓ Verified (${tenantUid.substring(0, 10)}...)`,
      doneDetail: 'Tenant organization bound with dedicated cryptographic envelope isolation boundary.'
    },
    {
      step: 2,
      title: 'Generate Scoped Enterprise API Keys',
      desc: 'Create secure API keys with specific RBAC scopes (tokenize, detokenize, gateway:llm) and configurable TTL expiration.',
      requirement: 'COMPULSORY',
      reqReason: 'Mandatory: Applications, microservices, and AI pipelines require authenticated API keys to process data.',
      link: '/apikeys',
      linkText: 'Configure API Keys',
      icon: <Key size={20} className="step-icon text-emerald" />,
      tag: isManager || isAdmin ? 'Configurable' : 'Manager/Admin',
      requiresManager: true,
      isDone: onboarding.step2Done,
      doneBadge: `✓ ${onboarding.keyCount} Active API Key(s)`,
      doneDetail: 'Scoped credentials configured for microservice & GenAI proxy authentication.'
    },
    {
      step: 3,
      title: 'Define Schemas & Sensitive Data Types',
      desc: 'Register schemas for payment cards, national IDs (SSN/Aadhaar), emails, and custom regex data structures.',
      requirement: 'OPTIONAL',
      reqReason: 'Optional for simple FPE and LLM Gateway scrubbing. Compulsory only for structured multi-field JSON payloads.',
      link: '/schemas',
      linkText: 'Manage Schema Registry',
      icon: <Database size={20} className="step-icon text-cyan" />,
      tag: isManager || isAdmin ? 'Data Schema' : 'Manager/Admin',
      requiresManager: true,
      isDone: onboarding.step3Done,
      doneBadge: `✓ ${onboarding.schemaCount} Schema(s) Registered`,
      doneDetail: 'Enterprise PII patterns, credit card schemas, and custom regex extractors ready.'
    },
    {
      step: 4,
      title: 'Configure De-tokenization & Masking Policies',
      desc: 'Enforce role-based access control. Define which microservice or employee role is authorized to view cleartext vs masked surrogates.',
      requirement: 'OPTIONAL',
      reqReason: 'Optional if default admin unmasking is sufficient. Compulsory for multi-role dynamic masking (e.g. Support vs Finance).',
      link: '/policies',
      linkText: 'Configure Zero-Trust Policies',
      icon: <Shield size={20} className="step-icon text-emerald" />,
      tag: isManager || isAdmin ? 'Zero-Trust' : 'Manager/Admin',
      requiresManager: true,
      isDone: onboarding.step4Done,
      doneBadge: `✓ ${onboarding.policyCount} Policy(ies) Enforced`,
      doneDetail: 'Zero-trust ABAC rule engine evaluating dynamic redaction and role permissions.'
    },
    {
      step: 5,
      title: 'Route Traffic via GenAI Gateway or REST SDK',
      desc: 'Intercept LLM prompts via the Zero-Trust GenAI Gateway, or call the low-latency REST endpoints directly in your applications.',
      requirement: 'COMPULSORY',
      reqReason: 'Mandatory: The production runtime integration point where cards are vaulted or LLM prompts are sanitized.',
      link: '/gateway',
      linkText: 'Launch GenAI Privacy Gateway',
      icon: <Cpu size={20} className="step-icon text-yellow" />,
      tag: 'Production',
      isDone: onboarding.step5Done,
      doneBadge: '✓ Privacy Gateway Live',
      doneDetail: 'Drop-in OpenAI proxy and REST cryptographic tokenization pipeline operational.'
    },
    {
      step: 6,
      title: 'Inspect Immutable Audit Evidence & SIEM Logs',
      desc: 'Review tamper-evident audit trails with cryptographic hash verification. Export compliance evidence for PCI DSS 4.0 and HIPAA audits.',
      requirement: 'OPTIONAL',
      reqReason: 'Optional / Recommended: Continuous audit trail verification and SIEM integration for annual compliance certifications.',
      link: '/dashboard',
      linkText: 'View Audit Logs & Metrics',
      icon: <CheckCircle2 size={20} className="step-icon text-emerald" />,
      tag: 'Compliance',
      isDone: onboarding.step6Done,
      doneBadge: `✓ ${onboarding.auditCount > 0 ? onboarding.auditCount + '+ Verified Logs' : 'Audit Ledger Active'}`,
      doneDetail: 'Cryptographically hashed audit records compiled for PCI DSS 4.0 & HIPAA certifications.'
    }
  ];

  const capabilities = [
    {
      title: 'Zero-Trust LLM Privacy Gateway',
      desc: 'Scrub PII, card numbers, and patient PHI before sending prompts to OpenAI, Claude, or Bedrock. Vaultize transparently restores cleartext into completions for authorized users.',
      icon: <Cpu size={28} className="text-cyan" />,
      highlight: 'Sub-15ms Latency'
    },
    {
      title: 'Format-Preserving Encryption (FPE)',
      desc: 'Transform sensitive 16-digit credit cards and 9-digit SSNs into valid, format-preserved tokens (NIST SP 800-38G FF1 / FF3-1) without modifying legacy DB schemas.',
      icon: <Lock size={28} className="text-emerald" />,
      highlight: 'NIST Standard'
    },
    {
      title: '90%+ Compliance Scope Descoping',
      desc: 'Because downstream databases and analytics data stores only store non-sensitive surrogate tokens, Qualified Security Assessors (QSAs) descope 90% of your architecture.',
      icon: <Shield size={28} className="text-emerald" />,
      highlight: 'PCI DSS 4.0 & HIPAA'
    },
    {
      title: 'Fine-Grained RBAC & Dynamic Masking',
      desc: 'Serve different views of the exact same data based on role: engineers see surrogate tokens, customer care sees partial masks (****-1234), and finance sees cleartext.',
      icon: <Zap size={28} className="text-yellow" />,
      highlight: 'Zero DB Duplication'
    },
    {
      title: 'Envelope Encryption & Cloud HSM',
      desc: 'Hardware-backed Master Keys (MK) in FIPS 140-2 Level 3 HSMs wrap tenant-specific Key Encryption Keys (KEK), deriving ephemeral record keys (DEK) for total breach immunity.',
      icon: <Database size={28} className="text-cyan" />,
      highlight: 'FIPS 140-2 Level 3'
    },
    {
      title: 'Tamper-Evident Audit Logging',
      desc: 'Every tokenization, detokenization, policy evaluation, and API call produces an immutable cryptographic ledger entry for instant compliance reporting.',
      icon: <CheckCircle2 size={28} className="text-emerald" />,
      highlight: 'Continuous Evidence'
    }
  ];

  return (
    <div className="getting-started-container">
      {/* Header Banner */}
      <header className="getting-started-header">
        <div className="gs-badge">
          <Rocket size={14} className="text-emerald" />
          <span>Kryda Cloud • Vaultize Setup &amp; Architecture Guide</span>
        </div>
        <h1 className="gs-title">Getting Started with Kryda Cloud Vaultize</h1>
        <p className="gs-subtitle">
          Everything you need to understand how Vaultize secures sensitive data, how the Zero-Trust LLM Gateway works,
          and the exact 6-step workflow to configure your production security enclave.
        </p>

        {/* Tenant Status Strip */}
        <div className="tenant-status-pill-strip">
          <div className="tenant-pill-item">
            <span className="pill-label">Organization:</span>
            <span className="pill-val">{tenant?.name || tenant?.email || 'Active Tenant'}</span>
          </div>
          <div className="tenant-pill-item">
            <span className="pill-label">Tenant UID:</span>
            <span className="pill-val font-mono">{tenant?.uid || 'Not Loaded'}</span>
          </div>
          <div className="tenant-pill-item">
            <span className="pill-label">Current Plan:</span>
            <span className="pill-val text-emerald font-bold">{tenant?.plan || 'Standard'}</span>
          </div>
          <div className="tenant-pill-item">
            <span className="pill-label">API Key:</span>
            <span className="pill-val font-mono text-cyan">
              {apiKey ? `${apiKey.substring(0, 8)}...` : 'Available in /apikeys'}
            </span>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="gs-nav-tabs">
        <button
          className={`gs-tab-btn ${activeTab === 'workflow' ? 'active' : ''}`}
          onClick={() => setActiveTab('workflow')}
        >
          <Rocket size={16} />
          <span>1. 6-Step Setup Workflow</span>
        </button>
        <button
          className={`gs-tab-btn ${activeTab === 'capabilities' ? 'active' : ''}`}
          onClick={() => setActiveTab('capabilities')}
        >
          <Shield size={16} />
          <span>2. What You Can Do (Capabilities Matrix)</span>
        </button>
        <button
          className={`gs-tab-btn ${activeTab === 'quickstart' ? 'active' : ''}`}
          onClick={() => setActiveTab('quickstart')}
        >
          <FileCode size={16} />
          <span>3. 30-Second Quickstart Code</span>
        </button>
      </div>

      {/* TAB 1: 6-Step Setup Workflow */}
      {activeTab === 'workflow' && (
        <section className="gs-tab-content">
          {/* Dynamic Live Onboarding Checklist Progress Meter */}
          <div className="onboarding-progress-widget">
            <div className="progress-widget-top">
              <div className="progress-widget-info">
                <div className="progress-widget-header-line">
                  <span className="progress-widget-title">Live Tenant Readiness &amp; Onboarding Progress</span>
                  <span className={`progress-status-chip ${allCompulsoryDone ? 'ready' : 'in-progress'}`}>
                    {allCompulsoryDone ? (
                      <>
                        <Check size={13} /> PRODUCTION READY
                      </>
                    ) : (
                      <>
                        <Clock size={13} /> SETUP IN PROGRESS
                      </>
                    )}
                  </span>
                </div>
                <p className="progress-widget-sub">
                  <strong>{totalDoneCount} of 6 milestones completed ({progressPercent}%)</strong> • {compulsoryDoneCount} of 3 compulsory milestones fulfilled.
                  {allCompulsoryDone ? ' Your security enclave is ready for live microservice and LLM traffic.' : ' Complete the remaining compulsory steps to initiate production vaulting.'}
                </p>
              </div>

              <button
                className="progress-refresh-btn"
                onClick={fetchOnboardingStatus}
                disabled={onboarding.refreshing}
                title="Query live tenant state from database"
              >
                <RefreshCw size={14} className={onboarding.refreshing ? 'spin-animation' : ''} />
                <span>{onboarding.refreshing ? 'Verifying...' : 'Verify Status'}</span>
              </button>
            </div>

            <div className="progress-bar-track">
              <div
                className={`progress-bar-fill ${allCompulsoryDone ? 'all-done' : ''}`}
                style={{ width: `${progressPercent}%` }}
              ></div>
            </div>

            <div className="progress-milestones-row">
              {steps.map((s) => (
                <div key={s.step} className={`milestone-tag ${s.isDone ? 'done' : 'pending'}`}>
                  <span className="milestone-num">{s.step}</span>
                  <span className="milestone-name">{s.tag}</span>
                  {s.isDone ? <Check size={12} className="milestone-check" /> : <span className="milestone-dash">—</span>}
                </div>
              ))}
            </div>
          </div>

          <div className="workflow-intro-card">
            <div className="workflow-intro-text">
              <h3>Step-by-Step Account Configuration Guide</h3>
              <p>
                Follow these six sequential steps to configure your tenant enclave, define your cryptographic schemas,
                protect your GenAI pipelines, and achieve full PCI DSS 4.0 / HIPAA compliance descoping.
              </p>
              <div className="workflow-summary-checklist-bar">
                <span className={`summary-pill compulsory ${allCompulsoryDone ? 'fulfilled' : ''}`}>
                  {compulsoryDoneCount}/3 Compulsory Steps Completed
                </span>
                <span className="summary-pill optional">
                  {steps.filter(s => s.requirement === 'OPTIONAL' && s.isDone).length}/3 Optional Steps Configured
                </span>
                <span className="summary-text">Follow the compulsory steps first to begin vaulting sensitive data immediately.</span>
              </div>
            </div>
            <div className="workflow-action-buttons">
              <Link to="/gateway" className="gs-btn-primary">
                <Cpu size={16} />
                <span>Test LLM Gateway</span>
              </Link>
              <Link to="/playground" className="gs-btn-outline">
                <BookOpen size={16} />
                <span>Open API Playground</span>
              </Link>
            </div>
          </div>

          <div className="steps-vertical-timeline">
            {steps.map((s) => (
              <div
                key={s.step}
                className={`step-card step-${s.requirement.toLowerCase()} ${s.isDone ? 'step-completed' : ''}`}
              >
                <div className="step-card-left">
                  <div className={`step-number-badge ${s.requirement.toLowerCase()} ${s.isDone ? 'badge-completed' : ''}`}>
                    {s.isDone ? <Check size={16} className="check-svg" /> : s.step}
                  </div>
                  <div className="step-icon-wrapper">{s.icon}</div>
                </div>

                <div className="step-card-body">
                  <div className="step-card-header">
                    <div className="step-title-group">
                      <h4 className="step-title">{s.title}</h4>
                      {s.isDone ? (
                        <span className="step-status-chip completed">
                          <Check size={12} /> {s.doneBadge}
                        </span>
                      ) : (
                        <span className={`requirement-badge ${s.requirement.toLowerCase()}`}>
                          {s.requirement === 'COMPULSORY' ? '● Compulsory (Action Needed)' : '○ Optional'}
                        </span>
                      )}
                    </div>
                    <span className="step-pill-tag">{s.tag}</span>
                  </div>
                  <p className="step-desc">{s.desc}</p>
                  
                  {s.isDone && s.doneDetail && (
                    <div className="step-completed-detail">
                      <span className="detail-icon">🛡️</span>
                      <span>{s.doneDetail}</span>
                    </div>
                  )}

                  <div className="step-req-reason">
                    <span className="reason-label">Requirement Rationale:</span> {s.reqReason}
                  </div>

                  <div className="step-action-row">
                    <Link to={s.link} className={`step-cta-link ${s.isDone ? 'link-completed' : ''}`}>
                      <span>{s.isDone ? `Manage / View ${s.tag}` : s.linkText}</span>
                      <ArrowRight size={14} />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* TAB 2: Capabilities Matrix */}
      {activeTab === 'capabilities' && (
        <section className="gs-tab-content">
          <div className="capabilities-grid">
            {capabilities.map((c, idx) => (
              <div key={idx} className="capability-card">
                <div className="cap-top-row">
                  <div className="cap-icon-box">{c.icon}</div>
                  <span className="cap-highlight-pill">{c.highlight}</span>
                </div>
                <h4 className="cap-title">{c.title}</h4>
                <p className="cap-desc">{c.desc}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* TAB 3: 30-Second Quickstart Code */}
      {activeTab === 'quickstart' && (
        <section className="gs-tab-content">
          <div className="quickstart-code-container">
            <div className="code-lang-selector-row">
              <div className="lang-buttons">
                <button
                  className={`lang-btn ${activeLang === 'curl' ? 'active' : ''}`}
                  onClick={() => setActiveLang('curl')}
                >
                  cURL (REST &amp; Streaming)
                </button>
                <button
                  className={`lang-btn ${activeLang === 'python' ? 'active' : ''}`}
                  onClick={() => setActiveLang('python')}
                >
                  Python (OpenAI SDK)
                </button>
                <button
                  className={`lang-btn ${activeLang === 'node' ? 'active' : ''}`}
                  onClick={() => setActiveLang('node')}
                >
                  Node.js / TypeScript
                </button>
                <button
                  className={`lang-btn ${activeLang === 'java' ? 'active' : ''}`}
                  onClick={() => setActiveLang('java')}
                >
                  Java (Spring Boot)
                </button>
              </div>

              <button
                className="copy-code-btn"
                onClick={() => copyToClipboard(codeSnippets[activeLang])}
              >
                <Copy size={14} />
                <span>{copied ? '✓ Copied' : 'Copy Code'}</span>
              </button>
            </div>

            <pre className="code-editor-block">
              <code>{codeSnippets[activeLang]}</code>
            </pre>

            <div className="code-credentials-footer">
              <div className="cred-item">
                <span className="cred-label">Authenticated Tenant:</span>
                <span className="cred-val font-mono">{tenantUid}</span>
              </div>
              <div className="cred-item">
                <span className="cred-label">Active API Key:</span>
                <span className="cred-val font-mono">
                  {apiKey ? `${apiKey.substring(0, 12)}...` : 'Using demo fallback key'}
                </span>
              </div>
              <div className="cred-action">
                <Link to="/apikeys" className="cred-link">
                  Manage API Keys &rarr;
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default GettingStarted;
